import { useState } from 'react'
import { PageContainer, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { Alert, Button, Tag } from 'antd'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { useMemberCreatorOptions, usePageTableNavigation } from '@/hooks/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getCreatedAtSearch, getDateRangeFormValue } from '@/utils/list-query.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'
import { BotCreateModal } from './bot-create-modal.tsx'
import { BotDeleteButton } from './bot-delete-button.tsx'
import { botListPersistence, botListSearchSchema, type BotListSearch } from './bot-list-search.ts'

const route = getRouteApi('/_authenticated/system-management/bots/')
type SearchValues = Omit<Partial<BotListSearch>, 'isEnabled'> & {
  isEnabled?: string
  createdAtRange?: ReturnType<typeof getDateRangeFormValue>
}
export function BotList() {
  const trpc = useTRPC()
  const access = useAccess()
  const cache = useQueryClient()
  const [open, setOpen] = useState(false)
  const { currentSession } = route.useRouteContext()
  const { search, navigateSearch } = usePersistedListQuery({
    persistence: botListPersistence,
    raw: route.useSearch(),
    accountId: currentSession.id,
    navigate: route.useNavigate()
  })
  const creator = useMemberCreatorOptions('bot.read', search.creatorMemberId)
  const bots = useQuery({ ...trpc.bot.list.queryOptions(search), placeholderData: keepPreviousData })
  const table = usePageTableNavigation<BotListSearch, SearchValues>({
    defaultSearch: botListSearchSchema.parse({}),
    search,
    navigate: navigateSearch,
    showTotal: (total) => `共 ${total} 个机器人`,
    getFormInitialValues: (query) => ({
      ...query,
      keyword: query.keyword ?? query.id,
      isEnabled: query.isEnabled?.toString(),
      createdAtRange: getDateRangeFormValue(query.createdFrom, query.createdTo)
    }),
    getSearchFromForm: (values) => ({
      ...values,
      id: undefined,
      isEnabled: values.isEnabled === undefined ? undefined : values.isEnabled === 'true',
      ...getCreatedAtSearch(values.createdAtRange ?? null)
    })
  })
  type Row = NonNullable<typeof bots.data>['data'][number]
  const columns: ProColumns<Row>[] = [
    {
      title: '关键词',
      dataIndex: 'keyword',
      hideInTable: true,
      fieldProps: { placeholder: '机器人名称或完整 ID', allowClear: true }
    },
    { title: 'ID', dataIndex: 'id', copyable: true, ellipsis: true, search: false },
    { title: '机器人', dataIndex: 'name', search: false },
    { title: '备注', dataIndex: 'remark', search: false, ellipsis: true },
    { title: '用途', dataIndex: 'kind', search: false, valueEnum: { customer: '客户客服', internal: '内部助手' } },
    { title: '企微机器人 ID', dataIndex: 'wecomBotId', search: false, ellipsis: true },
    {
      title: '状态',
      dataIndex: 'isEnabled',
      valueType: 'select',
      valueEnum: { true: '已启用', false: '已停用' },
      render: (_, row) => <Tag color={row.isEnabled ? 'success' : 'default'}>{row.isEnabled ? '已启用' : '已停用'}</Tag>
    },
    {
      title: '创建人',
      dataIndex: 'creatorMemberId',
      hideInTable: true,
      valueType: 'select',
      fieldProps: {
        allowClear: true,
        loading: creator.isLoading,
        options: creator.options,
        ...proRemoteSearch(creator.onSearch)
      }
    },
    { title: '创建人', dataIndex: ['creator', 'name'], search: false },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      valueType: 'dateTime',
      search: false,
      sorter: true,
      sortOrder: search.sortOrder === 'asc' ? 'ascend' : 'descend'
    },
    {
      title: '创建日期',
      dataIndex: 'createdAtRange',
      fieldProps: { presets: RANGE_PRESETS },
      valueType: 'dateTimeRange',
      hideInTable: true
    },
    {
      title: '操作',
      valueType: 'option',
      render: (_, row) => [
        <Link key="configure" to="/system-management/bots/$botId" params={{ botId: row.id }} search={{ tab: 'basic' }}>
          配置
        </Link>,
        <BotDeleteButton
          key="delete"
          bot={row}
          onDeleted={async () => {
            await cache.invalidateQueries(trpc.bot.list.queryFilter())
            if (bots.data?.data.length === 1 && search.current > 1) {
              await navigateSearch({ search: { ...search, current: search.current - 1 } })
            }
          }}
        />
      ]
    }
  ]
  return (
    <PageContainer
      title="机器人管理"
      breadcrumbRender={false}
      content="统一管理机器人基本信息、企微连接、MCP 接入和业务权限。"
    >
      {bots.isError && <Alert type="error" title={bots.error.message} showIcon className="mb-4" />}
      <ProTable<Row, SearchValues>
        rowKey="id"
        columns={columns}
        dataSource={bots.data?.data ?? []}
        loading={bots.isFetching}
        scroll={{ x: '65rem' }}
        formRef={table.formRef}
        form={{ initialValues: table.formInitialValues }}
        onSubmit={table.onSubmit}
        onReset={table.onReset}
        // Ant Design Table 的回调固定为四个参数，extra 用于区分排序与分页。
        // eslint-disable-next-line max-params
        onChange={(_, __, sorter, extra) => {
          if (extra.action !== 'sort') return
          const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter
          void navigateSearch({
            search: {
              ...search,
              current: 1,
              sortField: 'createdAt',
              sortOrder: currentSorter.order === 'ascend' ? 'asc' : 'desc'
            }
          })
        }}
        pagination={{ ...table.pagination, total: bots.data?.total ?? 0 }}
        options={{ reload: () => bots.refetch() }}
        toolBarRender={() =>
          access.all(['bot.create', 'bot.credential.update'])
            ? [
                <Button key="create" type="primary" onClick={() => setOpen(true)}>
                  新增机器人
                </Button>
              ]
            : []
        }
      />
      <BotCreateModal open={open} onClose={() => setOpen(false)} />
    </PageContainer>
  )
}
